/**
 * ScreenTimeTracker
 *
 * A singleton utility class that tracks how long a user spends on each
 * named section of the trainer flow.
 *
 * - Sections that are automated transitions are registered in SKIPPED_SECTIONS
 *   and are silently ignored so their time is never counted.
 * - Multiple sections can be active concurrently (e.g. Section 14 + AI Chat).
 * - Time is persisted to localStorage so it survives soft navigations.
 * - Page visibility changes (tab switch / minimize) are handled automatically:
 *   active sections pause when the tab is hidden and resume when it returns.
 */

// -------------------------------------------
// Section name constants
// -------------------------------------------

export const TRAINER_SECTIONS = {
  // Trackable sections
  SECTION_1: 'trainer_section_1_program_creator', // "Build structured workout programs 10x faster"
  SECTION_2: 'trainer_section_2_client_select', // "Who is this program for?"
  SECTION_3: 'trainer_section_3_energy_level', // "What's Emily's baseline energy level?"
  SECTION_4: 'trainer_section_4_activity', // "What's Emily's non-gym activity like?"
  SECTION_5: 'trainer_section_5_intensity', // "What's the intensity vibe for this program?"
  SECTION_6: 'trainer_section_6_fitness_level', // "What's their fitness level?"
  SECTION_7: 'trainer_section_7_training_goal', // "What is the training goal?"
  SECTION_8: 'trainer_section_8_movement_insights', // "Movement Insights"
  SECTION_9: 'trainer_section_9_program_duration', // "Let's set the program duration."
  SECTION_10: 'trainer_section_10_training_frequency', // "How often will they train?"
  SECTION_11: 'trainer_section_11_program_focus', // "What should this program focus on?"
  SECTION_12: 'trainer_section_12_injuries', // "Any injuries or limitations to keep in mind?"
  SECTION_13: 'trainer_section_13_generate_review', // "Let's create the program based on your responses"
  SECTION_14: 'trainer_section_14_program_generated', // /trainer/generated/ai
  SECTION_15: 'trainer_section_15_program_completed', // /trainer/generated/ai/completed
  AI_CHAT: 'trainer_ai_chat_assistant', // "How can I assist you today?" modal

  // Automated transitions (always skipped - user has no control)
  AUTOMATED_INTRO: 'automated_intro', // Step 2: "Hi! I'm MiniPT Core..."
  AUTOMATED_CLIENT_INTRO: 'automated_client_intro', // Step 4: "Let's build the base for Emily's workout plan."
  AUTOMATED_PROGRAM_INTRO: 'automated_program_intro', // Step 11: "Great! Now let's talk about the program."
} as const;

export type TrainerSectionKey = (typeof TRAINER_SECTIONS)[keyof typeof TRAINER_SECTIONS];

/**
 * Maps step number (from Trainer.tsx `step` state) to section name.
 * Steps that map to an AUTOMATED_ key are skipped automatically.
 */
export const STEP_TO_SECTION: Record<number, TrainerSectionKey> = {
  1: TRAINER_SECTIONS.SECTION_1,
  2: TRAINER_SECTIONS.AUTOMATED_INTRO,
  3: TRAINER_SECTIONS.SECTION_2,
  4: TRAINER_SECTIONS.AUTOMATED_CLIENT_INTRO,
  5: TRAINER_SECTIONS.SECTION_3,
  6: TRAINER_SECTIONS.SECTION_4,
  7: TRAINER_SECTIONS.SECTION_5,
  8: TRAINER_SECTIONS.SECTION_6,
  9: TRAINER_SECTIONS.SECTION_7,
  10: TRAINER_SECTIONS.SECTION_8,
  11: TRAINER_SECTIONS.AUTOMATED_PROGRAM_INTRO,
  12: TRAINER_SECTIONS.SECTION_9,
  13: TRAINER_SECTIONS.SECTION_10,
  14: TRAINER_SECTIONS.SECTION_11,
  15: TRAINER_SECTIONS.SECTION_12,
  16: TRAINER_SECTIONS.SECTION_13,
};

/**
 * Section names that must never be timed (automated transitions
 * where the user has no control over the UI).
 */
export const SKIPPED_SECTIONS = new Set<TrainerSectionKey>([
  TRAINER_SECTIONS.AUTOMATED_INTRO,
  TRAINER_SECTIONS.AUTOMATED_CLIENT_INTRO,
  TRAINER_SECTIONS.AUTOMATED_PROGRAM_INTRO,
]);

// -------------------------------------------
// Internal storage types
// -------------------------------------------

interface SectionRecord {
  /** Accumulated milliseconds (excluding the currently-active window). */
  totalMs: number;
  /** How many times this section was started. */
  visitCount: number;
  /**
   * Epoch ms when the current visit started, or `null` when paused / stopped.
   * This field is NOT persisted - it is always null after a page reload.
   */
  lastStartedAt: number | null;
}

interface PersistedData {
  [sectionName: string]: Omit<SectionRecord, 'lastStartedAt'>;
}

/** Public shape returned by getReport(). */
export interface SectionTimeData {
  sectionName: string;
  /** Human-readable label derived from the section key. */
  label: string;
  totalSeconds: number;
  visitCount: number;
}

// -------------------------------------------
// Storage key & label map
// -------------------------------------------

const STORAGE_KEY = 'minipt_screen_time_v1';
const SESSION_ID_KEY = 'minipt_behavior_session_id';

const SECTION_LABELS: Record<string, string> = {
  [TRAINER_SECTIONS.SECTION_1]: 'Section 1 - Program Creator Landing',
  [TRAINER_SECTIONS.SECTION_2]: 'Section 2 - Client Select',
  [TRAINER_SECTIONS.SECTION_3]: 'Section 3 - Baseline Energy Level',
  [TRAINER_SECTIONS.SECTION_4]: 'Section 4 - Non-Gym Activity',
  [TRAINER_SECTIONS.SECTION_5]: 'Section 5 - Intensity Vibe',
  [TRAINER_SECTIONS.SECTION_6]: 'Section 6 - Fitness Level',
  [TRAINER_SECTIONS.SECTION_7]: 'Section 7 - Training Goal',
  [TRAINER_SECTIONS.SECTION_8]: 'Section 8 - Movement Insights',
  [TRAINER_SECTIONS.SECTION_9]: 'Section 9 - Program Duration',
  [TRAINER_SECTIONS.SECTION_10]: 'Section 10 - Training Frequency',
  [TRAINER_SECTIONS.SECTION_11]: 'Section 11 - Program Focus',
  [TRAINER_SECTIONS.SECTION_12]: 'Section 12 - Injuries & Limitations',
  [TRAINER_SECTIONS.SECTION_13]: 'Section 13 - Generate Review',
  [TRAINER_SECTIONS.SECTION_14]: 'Section 14 - Program Generated (/trainer/generated/ai)',
  [TRAINER_SECTIONS.SECTION_15]: 'Section 15 - Program Completed (/trainer/generated/ai/completed)',
  [TRAINER_SECTIONS.AI_CHAT]: 'AI Chat - "How can I assist you today?"',
};

// -------------------------------------------
// ScreenTimeTracker class
// -------------------------------------------

export class ScreenTimeTracker {
  private static _instance: ScreenTimeTracker | null = null;

  private records: Map<string, SectionRecord> = new Map();

  /**
   * Set of section names whose timers are currently running.
   * Used to pause/resume on tab visibility change.
   */
  private activeSections: Set<string> = new Set();

  private constructor() {
    this._loadFromStorage();
    this._bindVisibilityChange();
  }

  /** Returns the singleton instance, creating it on the first call. */
  public static getInstance(): ScreenTimeTracker {
    if (!ScreenTimeTracker._instance) {
      ScreenTimeTracker._instance = new ScreenTimeTracker();
    }
    return ScreenTimeTracker._instance;
  }

  // ---------- Private helpers ----------

  private _ensureRecord(name: string): SectionRecord {
    if (!this.records.has(name)) {
      this.records.set(name, { totalMs: 0, visitCount: 0, lastStartedAt: null });
    }
    return this.records.get(name)!;
  }

  private _loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as PersistedData;
      for (const [name, persisted] of Object.entries(data)) {
        this.records.set(name, {
          totalMs: persisted.totalMs ?? 0,
          visitCount: persisted.visitCount ?? 0,
          lastStartedAt: null, // never restored from storage
        });
      }
    } catch {
      // Corrupted storage - start fresh
    }
  }

  private _saveToStorage(): void {
    try {
      const data: PersistedData = {};
      this.records.forEach((rec, name) => {
        data[name] = { totalMs: rec.totalMs, visitCount: rec.visitCount };
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore quota / security errors
    }
  }

  private _bindVisibilityChange(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._pauseAll();
      } else {
        this._resumeAll();
      }
    });
  }

  /**
   * Accumulate elapsed time for all active sections without removing
   * them from the active set (they will resume when the tab returns).
   */
  private _pauseAll(): void {
    const now = Date.now();
    this.activeSections.forEach((name) => {
      const rec = this.records.get(name);
      if (rec && rec.lastStartedAt !== null) {
        rec.totalMs += now - rec.lastStartedAt;
        rec.lastStartedAt = null;
      }
    });
    this._saveToStorage();
  }

  /** Restart the timer for all sections that were active before the pause. */
  private _resumeAll(): void {
    const now = Date.now();
    this.activeSections.forEach((name) => {
      const rec = this.records.get(name);
      if (rec && rec.lastStartedAt === null) {
        rec.lastStartedAt = now;
      }
    });
    // Storage will be updated on next stop call
  }

  // ---------- Public API ----------

  /**
   * Begin timing a section.
   * - Silently ignored for sections in `SKIPPED_SECTIONS`.
   * - Idempotent: safe to call multiple times while the section is active.
   */
  public startSection(sectionName: string): void {
    if (!sectionName || SKIPPED_SECTIONS.has(sectionName as TrainerSectionKey)) return;

    const rec = this._ensureRecord(sectionName);

    // Already running - don't double-count
    if (rec.lastStartedAt !== null) return;

    rec.lastStartedAt = Date.now();
    rec.visitCount += 1;
    this.activeSections.add(sectionName);
    this._saveToStorage();
  }

  /**
   * Stop timing a section and accumulate the elapsed duration.
   * - Silently ignored for sections in `SKIPPED_SECTIONS`.
   * - Safe to call on a section that is not currently running (no-op).
   */
  public stopSection(sectionName: string): void {
    if (!sectionName || SKIPPED_SECTIONS.has(sectionName as TrainerSectionKey)) return;

    const rec = this.records.get(sectionName);
    if (!rec || rec.lastStartedAt === null) return;

    rec.totalMs += Date.now() - rec.lastStartedAt;
    rec.lastStartedAt = null;
    this.activeSections.delete(sectionName);
    this._saveToStorage();
  }

  /**
   * Returns the total seconds spent on a section
   * (including any currently-active window).
   */
  public getSectionSeconds(sectionName: string): number {
    const rec = this.records.get(sectionName);
    if (!rec) return 0;
    const activeMs = rec.lastStartedAt !== null ? Date.now() - rec.lastStartedAt : 0;
    return Math.round((rec.totalMs + activeMs) / 1000);
  }

  /**
   * Returns the session ID previously persisted by `setSessionId()`,
   * or `null` if no session has been established yet.
   *
   * The session ID is generated by the backend on the first
   * `user_behavior_tracker` call and returned in the response.
   * Callers should pass this value on all subsequent requests so the
   * backend can upsert the same Firestore document.
   */
  public getStoredSessionId(): string | null {
    try {
      return localStorage.getItem(SESSION_ID_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Persists the session ID returned by the backend after the first
   * `user_behavior_tracker` POST request.
   * Call this once when you receive the response from the initial call.
   */
  public setSessionId(id: string): void {
    try {
      localStorage.setItem(SESSION_ID_KEY, id);
    } catch {
      // Ignore quota / security errors
    }
  }

  /**
   * Returns a report of all recorded (non-skipped) sections,
   * sorted alphabetically by section name.
   */
  public getReport(): SectionTimeData[] {
    const results: SectionTimeData[] = [];

    this.records.forEach((rec, name) => {
      // Exclude empty names and automated sections from the report
      if (!name || SKIPPED_SECTIONS.has(name as TrainerSectionKey)) return;

      const activeMs = rec.lastStartedAt !== null ? Date.now() - rec.lastStartedAt : 0;
      results.push({
        sectionName: name,
        label: SECTION_LABELS[name] ?? name,
        totalSeconds: Math.round((rec.totalMs + activeMs) / 1000),
        visitCount: rec.visitCount,
      });
    });

    return results.sort((a, b) => a.sectionName.localeCompare(b.sectionName));
  }

  /**
   * Logs the current screen-time report to the browser console as a formatted table.
   * Useful for debugging / QA - call `screenTimeTracker.logReport()` from DevTools.
   */
  public logReport(): void {
    const report = this.getReport();
    console.group('[ScreenTimeTracker] Screen Time Report');
    console.table(
      report.map((r) => ({
        Section: r.label,
        'Time (s)': r.totalSeconds,
        'Time (m:ss)': `${Math.floor(r.totalSeconds / 60)}:${String(r.totalSeconds % 60).padStart(2, '0')}`,
        Visits: r.visitCount,
      })),
    );
    console.groupEnd();
  }

  /**
   * Clears all tracked data (both in-memory and in localStorage).
   * Stops any currently-active sections first.
   */
  public reset(): void {
    this._pauseAll();
    this.records.clear();
    this.activeSections.clear();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
  }
}

/** Pre-created singleton - import and use this directly in hooks / components. */
export const screenTimeTracker = ScreenTimeTracker.getInstance();
